import React, { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import DashboardLayout from "@/components/DashboardLayout";
import CommissionTable from "@/components/CommissionTable";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { STAGES, stageIndex, stageLabel } from "@/lib/stages";

const Stat = ({ label, value, sub, testid }) => (
  <div className="bg-parchment border border-line p-6" data-testid={testid}>
    <div className="overline">{label}</div>
    <div className="h-serif text-4xl text-ink mt-2 tabular">{value}</div>
    {sub && <div className="text-xs text-sub mt-1 font-mono">{sub}</div>}
  </div>
);

export default function Dashboard() {
  const { user } = useAuth();
  const [deals, setDeals] = useState([]);
  const isSellerRedirect = user?.party_type === "seller" && user?.role !== "admin";

  useEffect(() => {
    if (isSellerRedirect) return;
    api.get("/deals").then((r) => setDeals(r.data)).catch(() => {});
  }, [isSellerRedirect]);

  // Sellers don't have access to ICPO intake; route them to the supplier desk.
  if (isSellerRedirect) {
    return <Navigate to="/dashboard/supplier" replace />;
  }

  const openDeals = deals.filter((d) => d.stage !== "commission_cleared").length;
  const closedDeals = deals.filter((d) => d.stage === "commission_cleared").length;

  return (
    <DashboardLayout testid="dashboard-overview">
      <div className="p-8 md:p-12 max-w-6xl">
        <div className="overline">Procurement Desk — Deal-Flow Intake</div>
        <h1 className="h-serif text-5xl text-ink mt-3" data-testid="dashboard-welcome">
          Welcome, {user.name.split(" ")[0]}.
        </h1>
        <p className="text-sub mt-4 max-w-2xl">
          Your account is unlocked for the deal-flow intake system. Every transaction
          you initiate here runs through the enforced legal sequence: ICPO → Soft Probe
          → FCO → NCNDA → IMFPA → SPA → Commission Cleared.
        </p>

        {user.role !== "admin" && (user.compliance?.kyc_status !== "cleared") && (
          <div className="mt-6 border-l-4 border-warn bg-parchment2 p-5 flex items-center justify-between gap-4" data-testid="compliance-banner">
            <div>
              <div className="overline !text-warn">Compliance required</div>
              <p className="text-sub text-sm mt-1 max-w-xl">
                {!user.compliance ? "Submit your KYC compliance profile before you can initiate deals." :
                  user.compliance.kyc_status === "submitted" ? "Your KYC profile is under review by our compliance desk." :
                  user.compliance.kyc_status === "flagged" ? "Compliance desk flagged your profile — please update it." :
                  user.compliance.kyc_status === "blocked" ? "Account blocked. Contact intake@campbellscommoditiesbrokeragellc.com." :
                  "Complete your KYC compliance profile to unlock deal submission."}
              </p>
            </div>
            {user.compliance?.kyc_status !== "blocked" && (
              <Link to="/dashboard/compliance" className="btn-primary !py-2 !px-4 text-sm shrink-0" data-testid="banner-go-compliance">
                {user.compliance ? "Update →" : "Submit profile →"}
              </Link>
            )}
          </div>
        )}

        <div className="grid sm:grid-cols-3 gap-5 mt-10">
          <Stat label="Account status" value="Approved"
                sub={user.role === "admin" ? "Admin role" : "Corporate buyer"} testid="stat-status" />
          <Stat label="Open deals" value={openDeals} sub="In the pipeline" testid="stat-open" />
          <Stat label="Cleared" value={closedDeals} sub="Commission wired" testid="stat-cleared" />
        </div>

        <section className="mt-14">
          <div className="flex items-end justify-between">
            <div>
              <div className="overline">Pipeline stages</div>
              <h2 className="h-serif text-3xl text-ink mt-2">Your active deals</h2>
            </div>
            <Link to="/dashboard/deals" className="btn-primary !py-2.5 !px-5 text-sm" data-testid="cta-new-deal">
              Submit ICPO →
            </Link>
          </div>

          {deals.length === 0 ? (
            <div className="border border-dashed border-line bg-parchment p-10 text-center mt-6">
              <p className="text-sub">No deals yet.</p>
              <Link to="/dashboard/deals" className="link mt-3 inline-block">Submit your first ICPO →</Link>
            </div>
          ) : (
            <div className="mt-6 border border-line bg-parchment divide-y divide-line" data-testid="deal-list">
              {deals.map((d) => {
                const idx = stageIndex(d.stage);
                const pct = Math.round(((idx + 1) / STAGES.length) * 100);
                return (
                  <Link
                    to={`/dashboard/deals/${d.deal_id}`}
                    key={d.deal_id}
                    className="block p-5 hover:bg-parchment2 transition-colors"
                    data-testid={`deal-row-${d.deal_id}`}
                  >
                    <div className="grid grid-cols-12 gap-4 items-center">
                      <div className="col-span-4">
                        <div className="h-serif text-lg text-ink">{d.commodity}</div>
                        <div className="font-mono text-[10px] text-sub mt-1">{d.deal_id}</div>
                      </div>
                      <div className="col-span-3 text-sm text-sub tabular">
                        {Number(d.volume).toLocaleString()} {d.unit} · {d.incoterm}
                      </div>
                      <div className="col-span-3">
                        <div className="font-mono text-[10px] text-oxblood uppercase tracking-wider">
                          {stageLabel(d.stage)}
                        </div>
                        <div className="mt-1 h-1 bg-line">
                          <div className="h-1 bg-oxblood" style={{ width: `${pct}%` }} />
                        </div>
                      </div>
                      <div className="col-span-2 text-right text-sm text-sub">{d.delivery_port}</div>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </section>

        <section className="mt-14" data-testid="dashboard-commission">
          <div className="overline">Commission schedule — your reference</div>
          <h2 className="h-serif text-3xl text-ink mt-2">What we charge</h2>
          <p className="text-sub text-sm mt-3 max-w-2xl leading-relaxed">
            Brokerage commission is paid at settlement, lodged through the IMFPA, and
            split off the top of the Buyer&apos;s Letter of Credit before the Seller
            receives their net payout. Ranges below cover standard trial volumes.
          </p>
          <div className="mt-5">
            <CommissionTable testid="dashboard-commission-table" />
          </div>
        </section>
      </div>
    </DashboardLayout>
  );
}
